import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Building2, ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { api, resolveImage } from "../lib/api";

const PLACEHOLDER = "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=srgb&fm=jpg&q=80&w=800";

function priceRange(p) {
  const fmt = (n) => {
    const num = Number(n);
    if (num >= 10000000) return `₹${(num / 10000000).toFixed(2).replace(/\.00$/, "")} Cr`;
    if (num >= 100000) return `₹${(num / 100000).toFixed(2).replace(/\.00$/, "")} L`;
    return `₹${num.toLocaleString("en-IN")}`;
  };
  if (p.priceMin && p.priceMax) return `${fmt(p.priceMin)} – ${fmt(p.priceMax)}`;
  if (p.priceMin) return `From ${fmt(p.priceMin)}`;
  if (p.priceMax) return `Up to ${fmt(p.priceMax)}`;
  return null;
}

export default function ProjectsCarousel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/projects");
        const items = Array.isArray(data.items) ? data.items : [];
        const uniq = items.filter((v, i, a) => a.findIndex((t) => t.id === v.id) === i);
        if (!cancelled) setItems(uniq);
      } catch (e) {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const nudge = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 340, behavior: "smooth" });
  };

  if (loading || items.length === 0) return null;

  return (
    <section className="relative py-14 bg-gradient-to-b from-white via-emerald-50/30 to-white z-0" data-testid="projects-carousel">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-emerald-700 bg-emerald-100 px-2.5 py-1 rounded-full mb-2">
              <Building2 className="h-3 w-3" /> Featured Projects
            </div>
            <h2 className="font-display font-bold text-2xl sm:text-3xl text-neutral-900">Curated Projects</h2>
            <p className="text-sm text-muted-foreground mt-1">Hand-picked residential &amp; commercial developments across Pataudi &amp; NCR.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => nudge(-1)} className="h-10 w-10 rounded-full border border-border bg-white hover:border-primary/40 flex items-center justify-center transition-colors" aria-label="Previous" data-testid="proj-carousel-prev">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button onClick={() => nudge(1)} className="h-10 w-10 rounded-full border border-border bg-white hover:border-primary/40 flex items-center justify-center transition-colors" aria-label="Next" data-testid="proj-carousel-next">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div
          ref={scrollerRef}
          className="flex gap-4 overflow-x-auto scroll-smooth no-scrollbar snap-x snap-mandatory"
          style={{ scrollbarWidth: "none" }}
        >
          {items.map((p) => {
            const img = (p.images && p.images[0]) ? resolveImage(p.images[0]) : PLACEHOLDER;
            const pr = priceRange(p);
            return (
              <Link
                key={p.id}
                to={`/projects/${p.id}`}
                className="group snap-start shrink-0 w-[300px] sm:w-[340px] bg-white rounded-2xl border border-neutral-200 overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(0,0,0,0.10)] hover:border-neutral-900/20"
                data-testid={`project-card-${p.id}`}
              >
                <div className="relative aspect-[4/3] bg-neutral-100 overflow-hidden">
                  <img src={img} alt={p.title} className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-[600ms] ease-out" loading="lazy" onError={(e) => { e.currentTarget.src = PLACEHOLDER; }} />
                  <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/45 to-transparent pointer-events-none" />
                  <span className="absolute top-3 left-3 bg-white/95 backdrop-blur text-neutral-900 text-[11px] font-semibold px-2.5 py-1 rounded-full shadow-sm">
                    {p.projectType}
                  </span>
                  <span className="absolute top-3 right-3 bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-sm">
                    {p.status}
                  </span>
                </div>
                <div className="p-4">
                  <h3 className="font-display font-semibold text-[15px] leading-snug text-neutral-900 line-clamp-1 group-hover:text-primary transition-colors">
                    {p.title}
                  </h3>
                  <p className="flex items-center gap-1 text-neutral-500 text-xs mt-1.5">
                    <MapPin className="h-3.5 w-3.5 shrink-0" /> {p.area ? `${p.area}, ` : ""}{p.city}
                  </p>
                  {pr && (
                    <p className="text-sm font-semibold text-emerald-700 mt-2">{pr}</p>
                  )}
                  <p className="text-xs text-neutral-500 mt-2 line-clamp-2 min-h-[2.2em]">
                    {p.description}
                  </p>
                  <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary">
                    View project <ArrowUpRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
